class FrameBuilderSettings {
    constructor(settings) {
        this.settings = settings;
    }

    getPrintImageUrl(variantId) {
        return this.settings.printImageUrls[variantId];
    }

    getCustomerPhotoMinScale() {
        return this.settings.customerPhotoMinScale;
    }

    getAllPrintImageUrls() {
        const images = [];
        for (const prop in this.settings.printImageUrls) {
            images.push(this.settings.printImageUrls[prop]);
        }

        return images;
    }

    /**
     * @returns {top, right, bottom, left}
     */
    getCustomerPhotoProps(variantTitle) {
        return this.settings.customerPhotoProps[variantTitle];
    }

    /**
     * @returns {top, right, bottom, left}
     */
    getCustomerTextProps(variantTitle) {
        return this.settings.customerTextProps[variantTitle];
    }

    getAcceptedImageTypes() {
        return ['image/jpeg', 'image/bmp', 'image/png'];
    }

    getCustomerTextConfirm(hasText) {
        return hasText
            ? this.settings.confirmCustomText
            : this.settings.confirmNoCustomText;
    }
}

class ProductFrameBuilder extends HTMLElement {
    initialize() {
        const jsonSettings = JSON.parse(
            this.querySelector('script[data-config-json]').innerHTML
        );
        const jsonProduct = JSON.parse(
            this.querySelector('script[data-product-json]').innerHTML
        );

        this.elementsCache = {};
        this.modalDialog = this.closest('modal-dialog');
        this.settings = new FrameBuilderSettings(jsonSettings);
        this.product = new JsonProduct(jsonProduct);
        this.variant = this.product.getVariants().find(variant => variant.getId() === parseInt(this.dataset.variantId));
        this.printImageDimensions = null;
        this.customerText = [];
        this.customerTextOriginal = null;
        this.windowWidth = window.innerWidth;
        this.panzoomInstance = null;
        this.debouncedAfterPanzoomingEnds = BryanAnthonys.debounce(() => this.afterPanzoomingEnds(), 300);

        if (this.validateInitialization()) {
            this.modalDialog.addEventListener('modalDialogShown', () => {
                this.listenForVariantChange();
                this.changeCanvasSourceImage();
                this.positionCustomerAreas();
                this.showFrameEdgeImage();
                this.preloadPrintImages();
            });

            window.addEventListener('resize', () => {
                if (this.modalDialog.hasAttribute('open') && this.windowWidth !== window.innerWidth) {
                    this.windowWidth = window.innerWidth;
                    this.positionCustomerAreas();
                }
            });

            if (this.canvasImageSource.complete) {
                this.setPrintImageNaturalDimensions();
            } else {
                this.canvasImageSource.onload = () => this.setPrintImageNaturalDimensions();
            }

            this.uploadField.addEventListener('change', event => this.updateUploadedFile(event));
            if (this.customerTextTextarea) {
                this.setupCustomerTextTextarea();
            }

            this.finishCustomizationButton.addEventListener('click', () => this.createFinalPrintImage());
            this.setUpErrorPopups();
        } else {
            const configurationErrors = this.initErrors.filter(error => error.type === 'configuration');
            this.dispatchEvent(
                new CustomEvent('frameBuilderInitFailed', {
                    detail: {configurationErrors},
                    bubbles: true
                })
            );
            this.classList.add('hidden');
        }
    }

    validateInitialization() {
        this.initErrors = [];

        if (typeof panzoom === "undefined") {
            this.initErrors.push({
                type: 'configuration',
                error: 'ProductFrame :: You need to load panzoom before this'
            });
        }

        if (typeof this.canvasForMerge.toBlob === "undefined") {
            this.initErrors.push({
                type: 'compatibility',
                error: 'ProductFrame :: Canvas to Blob is not suported'
            });
        }

        if (window.location.hash === '#selfEditImage') {
            this.initErrors.push({
                type: 'compatibility',
                error: 'ProductFrame :: SelfEditHash in url so we are allowing the customer to upload their own image'
            });
        }

        if (!this.product.getId() || !this.product.getVariants().length) {
            this.initErrors.push({
                type: 'configuration',
                error: 'ProductFrame :: Product data could not be loaded'
            });
        }

        this.product.getVariants().forEach(variant => {
            if (!this.settings.getCustomerPhotoProps(variant.getTitle())) {
                this.initErrors.push({
                    type: 'configuration',
                    error: `ProductFrame :: Product variant ${variant.getTitle()} does not have rectangle props set. 
                            They can be set in the Personalized frames section on the product page.`
                });
            }
        });

        return this.initErrors.length === 0;
    }

    listenForVariantChange() {
        const observer = new MutationObserver(() => {
            if (this.variant.getId() !== parseInt(this.dataset.variantId)) {
                this.variant = this.product.getVariants().find(
                    variant => variant.getId() === parseInt(this.dataset.variantId)
                );

                this.changeCanvasSourceImage();
                this.showFrameEdgeImage();
            }
        });

        observer.observe(this, {subtree: false, childList: false, attributes: true});
    }

    changeCanvasSourceImage() {
        const imageUrl = this.settings.getPrintImageUrl(this.variant.getId());

        if (this.canvasImageSource.src !== imageUrl) {
            this.canvasImageSource.src = imageUrl;
            this.canvasImageSource.onload = () => this.setPrintImageNaturalDimensions();
        }
    }

    setPrintImageNaturalDimensions() {
        this.printImageDimensions = {
            width: this.canvasImageSource.naturalWidth,
            height: this.canvasImageSource.naturalHeight
        };

        const canvasPaddingPercent = parseFloat(this.builderWrapper.style.padding.replace('%', ''));
        const paddingInPixels = ((canvasPaddingPercent / 100) * this.canvasImageSource.naturalWidth) * 2;
        let wrapperPaddingBottom = (this.canvasImageSource.naturalHeight + paddingInPixels)
            / (this.canvasImageSource.naturalWidth + paddingInPixels);
        wrapperPaddingBottom = (wrapperPaddingBottom * 100).toFixed(2);
        this.builderWrapper.parentElement.style.paddingBottom = `${wrapperPaddingBottom}%`;

        this.positionCustomerAreas();
    }

    positionCustomerAreas() {
        if (this.printImageDimensions === null) {
            return;
        }

        const frameRatio = this.getPrintImageRatio();
        const customerPhotoProps = this.customerPhotoProps;
        const customerTextProps = this.settings.getCustomerTextProps(this.variant.getTitle());

        for (let prop in customerPhotoProps) {
            if (['width', 'height'].indexOf(prop) !== -1) {
                continue;
            }

            this.customerPhotoArea.style[prop] = Math.round(customerPhotoProps[prop] * frameRatio) + 'px';
        }

        if (this.customerTextRenderArea) {
            for (let prop in customerTextProps) {
                this.customerTextRenderArea.style[prop] = Math.round(customerTextProps[prop] * frameRatio) + 'px';
            }

            this.customerTextRenderAreaCanvas.width = this.canvasImageSource.naturalWidth
                - customerTextProps.left - customerTextProps.right;
            this.customerTextRenderAreaCanvas.height = this.canvasImageSource.naturalHeight
                - customerTextProps.top - customerTextProps.bottom;

            this.renderCustomerText(this.customerText);
        }
    }

    getPrintImageRatio() {
        return this.canvasImageSource.offsetWidth / this.printImageDimensions.width;
    }

    renderCustomerText() {
        const textLines = this.customerText;
        const canvas = this.customerTextRenderAreaCanvas;
        const fontSize = 25;
        const lineSpacing = 40;
        const fontSpacing = String.fromCharCode(8202) + String.fromCharCode(8202);
        const context = canvas.getContext("2d");
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.font = `normal ${fontSize}px carentro`;
        context.fillStyle = '#000000';
        context.textAlign = "center";

        const x = canvas.width / 2;
        let y = canvas.height / 2;
        textLines.forEach((textLine, index) => {
            if (textLines.length !== 1) {
                y = index === 0
                    ? (canvas.height / 2 - (lineSpacing + fontSize) / 2)
                    : (canvas.height / 2 + (lineSpacing + fontSize) / 2);
            }
            const spacedText = textLine.split("").join(fontSpacing);
            context.fillText(spacedText, x, y);
        });
    }

    showFrameEdgeImage() {
        const variantTitle = this.variant.getTitle();

        this.frameEdgeImages.forEach(frameImage => {
            if (frameImage.dataset.canvasFrame === variantTitle) {
                frameImage.classList.remove('hidden');
                frameImage.src = frameImage.dataset.src;
                this.builderWrapper.style.padding = frameImage.dataset.padding;
            } else {
                frameImage.classList.add('hidden');
            }
        });
    }

    preloadPrintImages() {
        this.settings.getAllPrintImageUrls().forEach(imageUrl => {
            const img = document.createElement('img');
            img.loading = 'eager';
            img.src = imageUrl;
        });
    }

    updateUploadedFile(event) {
        const files = event.target.files;
        if (!files || !files.length) {
            return;
        }

        const file = files[0];
        if (this.settings.getAcceptedImageTypes().indexOf(file.type) === -1) {
            alert('The uploaded file is not an image or an accepted image type (jpeg, png or bmp).');

            return;
        }

        this.enablePanzoomArea(URL.createObjectURL(file));

        event.target.value = '';
    }

    enablePanzoomArea(dataUrl) {
        if (this.panzoomInstance !== null) {
            this.panzoomInstance.dispose();
            this.panzoomInstance = null;
        }

        this.customerPhotoAreaImage.onload = () => {
            const minSize = this.getMinAllowedCustomerImageSize();

            if (this.customerPhotoAreaImage.naturalWidth < minSize.width
                || this.customerPhotoAreaImage.naturalHeight < minSize.height) {
                alert(`The uploaded image size ${this.customerPhotoAreaImage.naturalWidth} by ${this.customerPhotoAreaImage.naturalHeight}`
                    + ` is too small. The minimum allowed size is ${Math.ceil(minSize.width)} by ${Math.ceil(minSize.height)} px.`);

                return;
            }

            this.customerPhotoAreaImage.style.visibility = 'visible';
            this.customerPhotoAreaImage.style.width = 'auto';
            this.customerPhotoAreaImage.style.maxWidth = 'auto';
            this.customerPhotoAreaImage.style.height = '100%';

            this.panzoomInstance = panzoom(this.customerPhotoAreaImage, {
                maxZoom: 3.5,
                minZoom: 1,
                smoothScroll: false
            });

            const observer = new MutationObserver((mutationList) => {
                const styleMutation = mutationList.find(mutationRecord => {
                    return mutationRecord.type === 'attributes' && mutationRecord.attributeName === 'style';
                });

                if (styleMutation) {
                    this.debouncedAfterPanzoomingEnds();
                }
            });
            observer.observe(this.customerPhotoAreaImage, {attributes: true, childList: false, subtree: false});

            this.finishCustomizationButton.disabled = false;
            this.uploadButton.querySelector('span').innerText = this.uploadButton.dataset.reuploadText;
        };

        this.customerPhotoAreaImage.src = dataUrl;
    }

    getMinAllowedCustomerImageSize() {
        const ratio = this.getPrintImageRatio() / (this.settings.getCustomerPhotoMinScale() / 100);
        const width = this.customerPhotoArea.clientWidth / ratio;
        const height = this.customerPhotoArea.clientHeight / ratio;

        return {width, height};
    }

    afterPanzoomingEnds() {
        if (!this.panzoomInstance) {
            return;
        }

        const issues = this.checkPanzoomLimits('autocorrect');

        if (issues.indexOf('resolution') !== -1) {
            this.customerPhotoErrorButtons.forEach(error => error.classList.remove('hidden'));
        } else {
            this.customerPhotoErrorButtons.forEach(error => error.classList.add('hidden'));
        }
    }

    checkPanzoomLimits(autoCorrect) {
        const transform = this.panzoomInstance.getTransform();
        const imageProps = this.getCustomerImageMeta();

        let newX = null, newY = null;

        if (transform.x > 0) {
            newX = 0;
        } else if (imageProps.right < 0) {
            newX = transform.x - (imageProps.right * imageProps.canvasImageScale);
        }

        if (transform.y > 0) {
            newY = 0;
        } else if (imageProps.bottom < 0) {
            newY = transform.y - (imageProps.bottom * imageProps.canvasImageScale);
        }

        if (autoCorrect && (newX !== null || newY !== null)) {
            if (newX === null) {
                newX = transform.x;
            }
            if (newY === null) {
                newY = transform.y;
            }
            this.panzoomInstance.moveTo(newX, newY);
            this.builderErrorInstructions.classList.remove('hidden');
        } else {
            this.builderErrorInstructions.classList.add('hidden');
        }

        const issues = [];

        if (!autoCorrect && (newX !== null || newY !== null)) {
            issues.push('position');
        }

        const minSize = this.getMinAllowedCustomerImageSize();
        if (imageProps.width < minSize.width || imageProps.height < minSize.height) {
            issues.push('resolution');
        }

        return issues;
    }

    /**
     * Returns details about the customer photo in natural pixels
     */
    getCustomerImageMeta() {
        const transform = this.panzoomInstance.getTransform();
        const canvasScale = (this.customerPhotoArea.clientHeight / this.customerPhotoAreaImage.naturalHeight);
        const canvasImageScale = canvasScale * transform.scale;

        const details = {
            left: Math.abs(transform.x) / canvasImageScale,
            top: Math.abs(transform.y) / canvasImageScale,
            width: this.customerPhotoArea.clientWidth / canvasImageScale,
            height: this.customerPhotoArea.clientHeight / canvasImageScale,
            canvasScale: canvasScale,
            canvasImageScale: canvasImageScale
        };

        details.right = this.customerPhotoAreaImage.naturalWidth - details.left - details.width;
        details.bottom = this.customerPhotoAreaImage.naturalHeight - details.top - details.height;

        return details;
    }

    setupCustomerTextTextarea() {
        this.customerTextTextarea.addEventListener('focus', () => {
            this.customerTextRenderArea.classList.add('builder-canvas__custom-text--editing');
            this.builderWrapper.parentElement.classList.add('builder-canvas-container--editing-text');
            this.positionCustomerAreas();
            // in case the code runs before the new css finished applying
            setTimeout(() => this.positionCustomerAreas(), 100);
        });

        this.customerTextTextarea.addEventListener('blur', () => {
            this.customerTextTextarea.value = this.customerText.join('\n');
            this.customerTextRenderArea.classList.remove('builder-canvas__custom-text--editing');
            this.builderWrapper.parentElement.classList.remove('builder-canvas-container--editing-text');
            this.positionCustomerAreas();
            // in case the code runs before the new css finished applying
            setTimeout(() => this.positionCustomerAreas(), 100);
        });

        this.customerTextTextarea.addEventListener('input', event => this.updateCustomerText(event.target.value));

        this.customerTextOriginal = this.customerTextTextarea.value;
        this.updateCustomerText(this.customerTextOriginal);

        this.querySelectorAll('[data-close-frame-text-editor]').forEach(textEditorToggle => {
            const detailsDisclosure = textEditorToggle.closest('[data-frame-text-editor]');
            if (!detailsDisclosure) {
                return;
            }

            textEditorToggle.addEventListener('click', () => {
                detailsDisclosure.removeAttribute('open');
                const summary = detailsDisclosure.querySelector('summary');
                if (this.customerText.length > 0) {
                    summary.querySelector('[data-btn-text]').innerText = summary.dataset.editText;
                } else {
                    summary.querySelector('[data-btn-text]').innerText = summary.dataset.addText;
                }
            });
        });

        this.querySelector('[data-clear-frame-text-editor]').addEventListener('click', () => {
            this.customerTextTextarea.value = '';
            this.customerTextTextarea.dispatchEvent(new Event('input'));
        });

        this.customerTextRenderArea.addEventListener('click', () => {
            this.querySelector('[data-frame-text-editor]').setAttribute('open', '');
            this.customerTextTextarea.focus();
        });
    }

    updateCustomerText(customerText) {
        const emoticons = /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g;
        this.customerText = customerText
            .replace(/</g, "")
            .replace(/>/g, "")
            .replace(emoticons, '')
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(line => line.length > 0);

        const lineCharLimit = 24;
        const flashErrorCue = textarea => {
            textarea.style.background = '#ffdcdc';
            setTimeout(() => textarea.style.background = '#fff', 500);
        };

        if (this.customerText.length > 2) {
            this.customerText = [this.customerText[0], this.customerText[1]];
            this.customerTextTextarea.value = this.customerText.join("\n");
            flashErrorCue(this.customerTextTextarea);
        } else {
            let changed = false;

            if (this.customerText[0] && this.customerText[0].length > lineCharLimit) {
                this.customerText[0] = this.customerText[0].substring(0, lineCharLimit);
                changed = true;
            }

            if (this.customerText[1] && this.customerText[1].length > lineCharLimit) {
                this.customerText[1] = this.customerText[1].substring(0, lineCharLimit);
                changed = true;
            }

            if (changed) {
                this.customerTextTextarea.value = this.customerText.join("\n");
                flashErrorCue(this.customerTextTextarea);
            }
        }

        this.renderCustomerText();

        if (this.customerText.join("\n") === this.customerTextOriginal) {
            this.customerTextRenderArea.classList.add('builder-canvas__custom-text--original');
        } else {
            this.customerTextRenderArea.classList.remove('builder-canvas__custom-text--original');
        }
    }

    createFinalPrintImage() {
        if (!this.panzoomInstance) {
            alert('You have to upload your image first');

            return;
        }

        const issues = this.checkPanzoomLimits();

        if (issues.indexOf('position') !== -1) {
            alert('Please properly position the image inside the canvas before saving.');

            return;
        }

        if (issues.indexOf('resolution') !== -1) {
            this.customerPhotoErrorButtons.forEach(button => button.classList.remove('hidden'));
            const modalOpenerButton = this.customerPhotoErrorButtons[0].querySelector('button');
            if (modalOpenerButton) {
                modalOpenerButton.click();
            }

            return;
        }

        try {
            this.mergeImages();
            const mergedImageUrl = this.canvasForMerge.toDataURL("image/png");
            this.customizedForVariantId = this.variant.getId();
            this.dispatchEvent(new CustomEvent('createdFinalPrintImage', {
                detail: {
                    mergedImageUrl,
                    canvasForMerge: this.canvasForMerge,
                    variant: this.variant,
                    product: this.product,
                    customerText: this.customerTextTextarea ? this.customerText : null
                },
                bubbles: true
            }));
        } catch (error) {
            alert(BryanAnthonys.strings.general.genericAjaxError);

            console.error(error);
        }
    }

    mergeImages() {
        if (!this.canvasImageSource.complete) {
            throw "Print image not yet loaded";
        }

        const canvasContext = this.canvasForMerge.getContext('2d');
        canvasContext.clearRect(0, 0, this.canvasForMerge.width, this.canvasForMerge.height);

        // Drawing the big image accross the entire canvas
        this.canvasForMerge.width = this.canvasImageSource.naturalWidth;
        this.canvasForMerge.height = this.canvasImageSource.naturalHeight;
        canvasContext.drawImage(
            this.canvasImageSource, 0, 0,
            this.canvasImageSource.naturalWidth,
            this.canvasImageSource.naturalHeight
        );

        // Drawing the customer image inside the rectangle
        const imageProps = this.getCustomerImageMeta();
        const canvasProps = this.customerPhotoProps;
        canvasContext.drawImage(
            this.customerPhotoAreaImage,
            imageProps.left, imageProps.top, imageProps.width, imageProps.height,
            canvasProps.left, canvasProps.top, canvasProps.width, canvasProps.height
        );

        if (this.customerTextTextarea && this.customerText.length) {
            // Merge the custom text canvas to the final image
            const customerTextProps = this.settings.getCustomerTextProps(this.variant.getTitle());
            canvasContext.drawImage(
                this.customerTextRenderAreaCanvas,
                customerTextProps.left, customerTextProps.top, this.customerTextRenderAreaCanvas.width, this.customerTextRenderAreaCanvas.height
            );
        }
    }

    reset() {
        if (this.panzoomInstance !== null) {
            this.panzoomInstance.dispose();
            this.panzoomInstance = null;
        }
        this.finishCustomizationButton.disabled = true;
        this.uploadButton.querySelector('span').innerText = this.uploadButton.dataset.uploadText;
        this.customerPhotoAreaImage.style.visibility = 'hidden';
        this.customerText = [];
        if (this.customerTextTextarea) {
            this.customerTextTextarea.value = '';
        }
    }

    setUpErrorPopups() {
        document.addEventListener('modalDialogShown', event => {
            if (event.target.id === 'ProductCustomizerErrors') {
                const minSize = this.getMinAllowedCustomerImageSize();
                const requiredResolution = `${Math.ceil(minSize.width)} x ${Math.ceil(minSize.height)}`;
                const imageProps = this.getCustomerImageMeta();
                const currentResolution = `${Math.round(imageProps.width)} x ${Math.round(imageProps.height)}`;

                const content = event.target.querySelector('[data-product-frame-builder-errors-modal-content]');
                // eslint-disable-next-line no-unsanitized/property
                content.innerHTML = content.innerHTML
                    .replace(/\[current_resolution]/g, currentResolution)
                    .replace(/\[required_resolution]/g, requiredResolution);
            }
        });
    }

    getInnerElement(selector) {
        if (!this.elementsCache[selector]) {
            this.elementsCache[selector] = this.querySelector(selector);
        }

        return this.elementsCache[selector];
    }

    getInnerElements(selector) {
        if (!this.elementsCache[`list-${selector}`]) {
            this.elementsCache[`list-${selector}`] = this.querySelectorAll(selector);
        }

        return this.elementsCache[`list-${selector}`];
    }

    get canvasForMerge() {
        return this.getInnerElement('canvas[data-canvas-for-merge]');
    }

    get canvasImageSource() {
        return this.getInnerElement('img[data-canvas-image-source]');
    }

    get builderWrapper() {
        return this.getInnerElement('[data-builder-wrapper]');
    }

    get customerPhotoArea() {
        return this.getInnerElement('[data-customer-photo-area]');
    }

    get customerPhotoAreaImage() {
        return this.getInnerElement('img[data-customer-photo-area-image]');
    }

    get customerTextRenderArea() {
        return this.getInnerElement('[data-customer-text-render-area]');
    }

    get customerTextRenderAreaCanvas() {
        return this.getInnerElement('[data-customer-text-render-area-canvas]');
    }

    get uploadField() {
        return this.getInnerElement('[data-image-upload-field]');
    }

    get uploadButton() {
        return this.getInnerElement('[data-image-upload-button]');
    }

    get finishCustomizationButton() {
        return this.getInnerElement('[data-finish-customization-button]');
    }

    get builderErrorInstructions() {
        return this.getInnerElement('[data-builder-error-instructions]');
    }

    get customerTextTextarea() {
        return this.getInnerElement('#customer-text-canvas-textarea');
    }

    get frameEdgeImages() {
        return this.getInnerElements('[data-canvas-frame]');
    }

    get customerPhotoErrorButtons() {
        return this.getInnerElements('[data-modal="#ProductCustomizerErrors"]');
    }

    get customerPhotoProps() {
        const customerPhotoProps = this.settings.getCustomerPhotoProps(this.variant.getTitle());

        if (!Object.prototype.hasOwnProperty.call(customerPhotoProps, 'width')) {
            customerPhotoProps.width = this.printImageDimensions.width - customerPhotoProps.left - customerPhotoProps.right;
            customerPhotoProps.height = this.printImageDimensions.height - customerPhotoProps.top - customerPhotoProps.bottom;
        }

        return customerPhotoProps;
    }
}

customElements.define('product-frame-builder', ProductFrameBuilder);

customElements.define('product-frame-builder-initializer', class extends HTMLElement {
    connectedCallback() {
        if (typeof this.parentElement.initialize === 'function') {
            this.parentElement.initialize();
        }
    }
});